"use client";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";

import NoDataImage from "@/assets/error/no-data-found.jpg";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { USERS_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetUsers from "@/hooks/requests/user/use-get-users";
import { useButtonPermission } from "@/hooks/use-button-permission";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import IUser from "@/interfaces/user.interface";
import { UsersMessages } from "@/messages/users/user.messages";

import UserFiltersForm from "./components/UserFiltersForm";
import { UserTable } from "./components/UserTable/index";
import { initialFilterValues } from "./constants";

type Props = {
  messages: UsersMessages;
};

const LOADING_SPINNER_SIZE = 50;
const CARD_MARGIN_TOP = 16;
const FIRST_PAGE = 0;

function UserPage({ messages }: Props) {
  const pagination = usePagination();
  const router = useNavigate();
  const { pageNumber, pageSize, changePage } = pagination;
  const { filters, changeFilters } = useFilters<IUser>({
    initialValue: initialFilterValues,
  });
  const { lang } = useLanguage();
  const { data: users, isLoading, error } = useGetUsers({ filters, pageNumber, pageSize });
  const userPermission = useButtonPermission(USERS_PERMISSIONS.CREATE);

  const shouldLoadUsersTable = !!users && !isLoading && !error && users.items.length > 0;
  const shouldLoadError = !users && !isLoading && !!error;
  const shouldLoadNoUsersFound = !!users && !isLoading && !error && users.items.length === 0;

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Formik
          initialValues={filters}
          validateOnChange={false}
          onSubmit={(filters) => {
            changePage(FIRST_PAGE);
            changeFilters(filters);
          }}
        >
          <UserFiltersForm messages={messages} />
        </Formik>
        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(CARD_MARGIN_TOP) })}>
          <Suspense
            isSuspended={isLoading}
            fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}
          >
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="bold">
                  {messages.userTableTitle}
                </MDTypography>

                <MDButton
                  variant="gradient"
                  color="dark"
                  endIcon={<AddCircleOutlineIcon />}
                  onClick={() => router(ROUTE_URLS.createUser(lang))}
                  disabled={userPermission.disabled}
                >
                  {messages.addUserButton}
                </MDButton>
              </MDBox>
              {!!shouldLoadUsersTable && (
                <UserTable pagination={pagination} data={users} messages={messages} />
              )}

              {!!shouldLoadError && (
                <AlertBlock
                  title={messages.userErrorToLoad}
                  description={error.message}
                  src={NoDataImage}
                  alt={messages.userNotFoundAlt}
                />
              )}

              {!!shouldLoadNoUsersFound && (
                <AlertBlock
                  title={messages.userNotFoundError}
                  description={messages.userNotFoundDescription}
                  src={NoDataImage}
                  alt={messages.userNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default UserPage;
